import { useState, useEffect, useRef, createContext, useContext } from "react";
import { saveSession, C, LOGO_BASE } from "./utils";
import DMPanel from "./DMPanel";
import CongressScreen from "./CongressScreen";
import CongressPlanner from "./CongressPlanner";
import ProductProfileScreen from "./Product_profile";
import ClinicalTimeline from "./ClinicalTimeline";
import EventsTimeline from "./EventsTimeline";
import TimelinesScreen from "./Timelines";
import EchoScreen from "./Echo";
import ReviewPublish from "./ReviewPublish";
import { BellButton } from "./NotificationPanel";
import VectorSearch, { AIChatOverlay } from "./VectorSearch";
import AdvancedFilterScreen, { FilterPanel, FilterChipsBar, EMPTY_FILTER, hasActiveFilters } from "./AdvancedFilter";
import AtAGlance from "./AtAGlance";
import AdminCorner from "./AdminCorner";
import UserManagement from "./UserManagement";
import UserSettings from "./UserSettings";
import DashboardHome from "./DashboardHome";
import NewsFlashScreen from "./NewsFlash";
import WorkshopScreen from "./Workshop";
import { GalleryScreen, ListScreen, ClinicalTrialsScreen, EventsScreen,
         NEWS_TYPES, REPORTS_TYPES, KITS_TYPES } from "./Dashboard";
// ─── Theme context — shared with all content screens ─────────────────────────
export const ThemeCtx    = createContext(null);
export const useTheme    = () => useContext(ThemeCtx);
// ─── Nav items — edit here to add/remove menu entries ────────────────────────
export const NAV_ITEMS = [
  { id: "home",           label: "Dashboard",       key: null,              icon: "home"         },
  { id: "ataglance",      label: "At a Glance",     key: "AT_A_GLANCE",     icon: "ataglance"    },
  { id: "clinicaltrials", label: "Clinical Trials",  key: "CLINICAL_TRIALS", icon: "trials"       },
  { id: "productprofile", label: "Product Profiles", key: "PRODUCT",         icon: "product"      },
  { id: "workshop",       label: "Workshop",         key: "WORKSHOP",        icon: "workshop"     },
  { id: "newsletter",     label: "NewsFlash",        key: "NEWSLETTER",      icon: "newsletter"   },
  { id: "timelines",      label: "Timelines",        key: "TIMELINES",       icon: "timelines"    },
  { id: "congress",       label: "Congress",         key: "CONGRESS",        icon: "congress"     },
  { id: "reports",        label: "Reports",          key: "REPORTS",         icon: "reports"      },
  { id: "gallery",        label: "Gallery",          key: "GALLERY",         icon: "gallery"      },
  { id: "news",           label: "News",             key: "NEWS",            icon: "news"         },
  { id: "past",           label: "Past Events",      key: "UPCOMING_EVENTS", icon: "past"         },
  { id: "upcoming",       label: "Upcoming Events",  key: "UPCOMING_EVENTS", icon: "upcoming"     },
  { id: "kits",           label: "KITS/KIQS",        key: "KITS_KIQS",       icon: "kits"         },
  { id: "echo",           label: "Echo",             key: "ECHO",            icon: "echo"         },
];
// Maps PAGES[].key → nav id (matches MYDEFAULT page key to route id)
const PAGE_KEY_TO_NAV = {
  "0":  "home",
  "1":  "home",          // CAPTURE → dashboard
  "2":  "gallery",
  "3":  "upcoming",
  "4":  "news",
  "5":  "reports",
  "6":  "congress",
  "7":  "kits",
  "8":  "clinicaltrials",
  "9":  "productprofile",
  "10": "timelines",
  "11": "newsletter",
  "12": "workshop",
  "13": "ataglance",
};
const NAV_KEY = (subId) => `pv_nav_${subId}`;
function getDefaultNav(activeSub) {
  const subId = activeSub?.SUBPROJECTID;
  if (subId) {
    const saved = sessionStorage.getItem(NAV_KEY(subId));
    if (saved) return saved;
  }
  const myDefault = activeSub?.MYDEFAULT;
  if (myDefault == null) return "ataglance";
  const navId = PAGE_KEY_TO_NAV[String(myDefault)];
  return navId || "ataglance";
}
// ─── Access check ─────────────────────────────────────────────────────────────
export const hasAccess = (sub, key) => !key || sub?.[key] === 1;
// ─── Sort options ─────────────────────────────────────────────────────────────
export const SORT_OPTIONS = [
  { value: "latest",        label: "Latest"            },
  { value: "likes",         label: "Most liked"        },
  { value: "comment",       label: "Most commented"    },
  { value: "latest_attach", label: "Recently attached" },
];
// ─── Icon component ───────────────────────────────────────────────────────────
export const Icon = ({ name, size = 14, color = "currentColor" }) => {
  const s = { width: size, height: size, flexShrink: 0 };
  if (name === "home")       return <svg style={s} viewBox="0 0 16 16" fill={color}><path d="M8 1L1 7h2v7h4v-4h2v4h4V7h2L8 1z"/></svg>;
  if (name === "gallery")    return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>;
  if (name === "search")     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
  if (name === "bell")       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
  if (name === "attach")     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>;
  if (name === "like")       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg>;
  if (name === "image")      return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>;
  if (name === "chat")       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
  if (name === "star")       return <svg style={s} viewBox="0 0 24 24" fill="#EF9F27"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;
  if (name === "chevron")    return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>;
  if (name === "ataglance")  return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/></svg>;
  if (name === "trials")     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v11M3 9h18M3 9v9a2 2 0 002 2h14a2 2 0 002-2V9"/><path d="M8 21l4-4 4 4"/></svg>;
  if (name === "product")    return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
  if (name === "workshop")   return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>;
  if (name === "newsletter") return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
  if (name === "timelines")  return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><circle cx="7" cy="12" r="2" fill={color} stroke="none"/><circle cx="12" cy="12" r="2" fill={color} stroke="none"/><circle cx="17" cy="12" r="2" fill={color} stroke="none"/><path d="M3 6h4M3 18h4M17 6h4M17 18h4"/></svg>;
  if (name === "congress")   return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
  if (name === "reports")    return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
  if (name === "news")       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8M15 18h-5M10 6h8v4h-8z"/></svg>;
  if (name === "past")       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 8 14"/><line x1="2" y1="12" x2="4" y2="12"/></svg>;
  if (name === "upcoming")   return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/></svg>;
  if (name === "kits")       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>;
  if (name === "echo")       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>;
  return <div style={{ ...s, borderRadius: "50%", background: color, opacity: 0.5 }} />;
};
// ─── Logo color extractor ─────────────────────────────────────────────────────
export function useLogoColors(logoUrl) {
  const [palette, setPalette] = useState(null);
  const cacheRef = useRef({});
  useEffect(() => {
    if (!logoUrl) { setPalette(null); return; }
    if (cacheRef.current[logoUrl]) { setPalette(cacheRef.current[logoUrl]); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 64; canvas.height = 64;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, 64, 64);
        const data = ctx.getImageData(0, 0, 64, 64).data;
        const buckets = {};
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
          if (a < 80) continue;
          const max = Math.max(r,g,b), min = Math.min(r,g,b);
          const l = (max+min)/2/255;
          const s = max === min ? 0 : (max-min)/(255*(1-Math.abs(2*l-1)));
          if (l > 0.92 || l < 0.08 || s < 0.18) continue;
          let h = 0;
          if (max !== min) {
            if (max === r) h = ((g-b)/(max-min)+6)%6;
            else if (max === g) h = (b-r)/(max-min)+2;
            else h = (r-g)/(max-min)+4;
          }
          const hb = Math.round(h*60/15)*15;
          buckets[hb] = (buckets[hb]||0)+1;
        }
        const sorted = Object.entries(buckets).sort((a,b)=>b[1]-a[1]);
        if (!sorted.length) { setPalette(null); return; }
        const dominant = parseInt(sorted[0][0]);
        const hToRgb = (h,s,l) => {
          const c = (1-Math.abs(2*l-1))*s;
          const x = c*(1-Math.abs((h/60)%2-1));
          const m = l-c/2;
          let r=0,g=0,b=0;
          if (h<60){r=c;g=x;}else if(h<120){r=x;g=c;}else if(h<180){g=c;b=x;}
          else if(h<240){g=x;b=c;}else if(h<300){r=x;b=c;}else{r=c;b=x;}
          const toH = v=>Math.round((v+m)*255).toString(16).padStart(2,"0");
          return `#${toH(r)}${toH(g)}${toH(b)}`;
        };
        const p = {
          primary:    hToRgb(dominant, 0.65, 0.38),
          accent:     hToRgb(dominant, 0.70, 0.45),
          accentLight:hToRgb(dominant, 0.50, 0.92),
          sidebar:    `linear-gradient(180deg, ${hToRgb(dominant,0.55,0.22)} 0%, ${hToRgb(dominant,0.45,0.18)} 100%)`,
          sidebarBg:  hToRgb(dominant, 0.12, 0.97),
          headerBg:   "#ffffff",
          avatarBg:   hToRgb(dominant, 0.60, 0.40),
          activeBar:  hToRgb(dominant, 0.75, 0.52),
        };
        cacheRef.current[logoUrl] = p;
        setPalette(p);
      } catch { setPalette(null); }
    };
    img.onerror = () => setPalette(null);
    img.src = logoUrl;
  }, [logoUrl]);
  return palette;
}
// ─── Build theme from logo palette ────────────────────────────────────────────
export function buildTheme(logoColors) {
  return {
    primary:     logoColors?.primary     || C.primary,
    sidebar:     logoColors?.sidebarBg   || C.sidebar,
    sidebarGrad: logoColors?.sidebar     || C.sidebar,
    accent:      logoColors?.accent      || C.primary,
    accentLight: logoColors?.accentLight || "#EBF3FB",
    headerBg:    logoColors?.headerBg    || "#ffffff",
    avatarBg:    logoColors?.avatarBg    || C.primary,
    activeBar:   logoColors?.activeBar   || "#378ADD",
    tileGrad:    logoColors?.sidebar     || `linear-gradient(135deg, ${C.primary} 0%, #1a6fba 100%)`,
  };
}
// ─── NavItem ──────────────────────────────────────────────────────────────────
function NavItem({ item, active, onClick, theme }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: "10px",
        padding: "7px 12px", margin: "1px 6px", borderRadius: "7px",
        border: "none", cursor: "pointer", textAlign: "left", width: "calc(100% - 16px)",
        background: active ? theme.accentLight || "#EBF3FB" : hovered ? "#EEF2F7" : "transparent",
        color: active ? theme.primary || C.primary : C.muted,
        fontWeight: active ? "600" : "400", fontSize: "12.5px",
        transition: "all 0.15s",
        position: "relative",
      }}>
      {active && (
        <div style={{ position: "absolute", left: 0, top: "20%", bottom: "20%", width: "3px", borderRadius: "0 3px 3px 0", background: theme.activeBar || C.primary }} />
      )}
      <Icon name={item.icon} size={15} color={active ? theme.primary || C.primary : C.hint} />
      <span>{item.label}</span>
    </button>
  );
}
// ─── AppShell ─────────────────────────────────────────────────────────────────
export default function AppShell({ session, onLogout, overrideContent, hideNav=false, hideFilterButtons=false, allowNavOverride=false, hideSearchBar=false }) {
  const { user } = session;
  const subs       = [...new Map((user?.SUBPROJECTS||[]).map(s=>[s.SUBPROJECTID,s])).values()];
  const resolvedSub = subs.find(s => s.SUBPROJECTID === session?.subproject?.SUBPROJECTID) || subs[0] || null;
  const [activeSub, setActiveSub] = useState(resolvedSub);
  const [showProfile,      setShowProfile]      = useState(false);
  const [showDM,           setShowDM]           = useState(false);
  const [showAdminCorner,  setShowAdminCorner]  = useState(false);
  const [showPrivacy,      setShowPrivacy]      = useState(false);
  const [showSettings,     setShowSettings]     = useState(false);
  const dmRef = useRef(null);
  const profileRef = useRef(null);
  useEffect(()=>{
    if(!showProfile) return;
    const handler = (e) => { if(profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false); };
    const keyHandler = (e) => { if(e.key==="Escape") setShowProfile(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return ()=>{ document.removeEventListener("mousedown", handler); document.removeEventListener("keydown", keyHandler); };
  },[showProfile]);
  useEffect(()=>{
    if(!showDM) return;
    const keyHandler = (e) => { if(e.key==="Escape") setShowDM(false); };
    document.addEventListener("keydown", keyHandler);
    return ()=>document.removeEventListener("keydown", keyHandler);
  },[showDM]);
  const [activeNav,     setActiveNav]     = useState(() => getDefaultNav(resolvedSub));
  const [filterApplied, setFilterApplied] = useState(null);
  const [filterOpen,    setFilterOpen]    = useState(false);
  const prevNavRef = useRef(null);
  const [navSort,    setNavSort]    = useState(null);
  const [navCleared, setNavCleared] = useState(false);
  // ── NEW: AI search query state ────────────────────────────────────────────
  const [aiQuery, setAiQuery] = useState("");
  const [showAIChat,  setShowAIChat]  = useState(false);
  const [aiChatWidth, setAiChatWidth] = useState(680);
  const [aiMinimized, setAiMinimized] = useState(false);
  const [aiHistory,   setAiHistory]   = useState([]);
  const [aiThread,    setAiThread]    = useState(null);
  const [searchKey,   setSearchKey]   = useState(0); // chat history from VectorSearch
  // ─────────────────────────────────────────────────────────────────────────

  // ── Deep link: /pvision/askme?threadid=X — resume a Research Assistant thread
  useEffect(()=>{
    if(!user||!activeSub) return;
    const isAskMe = window.location.pathname.endsWith("/askme");
    if(!isAskMe) return;
    const p = new URLSearchParams(window.location.search);
    const threadId = p.get("threadid");
    if(!threadId) return;

    (async()=>{
      setShowAIChat(true);
      setAiHistory([]); // loading state — empty until response
      try {
        const fd = new FormData();
        fd.append("threadid",     threadId);
        fd.append("subprojectid", activeSub.SUBPROJECTID);
        fd.append("userid",       user.USERID);
        fd.append("mode",         "history"); // load existing thread history
        const r = await fetch("https://cip.medsphere.net/api/Vector_search",
          {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
        const res = await r.json();
        if(res.STATUS===1||res.STATUS==="1"){
          setAiThread(res.THREAD||threadId);
          if(Array.isArray(res.MESSAGES)&&res.MESSAGES.length){
            // API returns newest-first — reverse to chronological order for display
            setAiHistory([...res.MESSAGES].reverse());
          } else if(Array.isArray(res.DATA)&&res.DATA.length){
            setAiHistory(res.DATA);
          } else if(res.COMPLETION){
            setAiHistory([{ROLE:"assistant",VALUES_OF_CONTENT:res.COMPLETION}]);
          }
        }
      } catch(err){ console.error("askme deep-link failed:",err); }
      // clean the URL once handled
      window.history.replaceState({},"",window.location.pathname.replace(/\/askme$/,"")||"/");
    })();
  },[user, activeSub]);
  const removeFilterChip = (chipKey) => {
    setFilterApplied(f => {
      if(!f) return f;
      const n = {...f};
      if(chipKey==="date"){ n.fromDate=""; n.toDate=""; }
      else if(chipKey==="kw") n.keyword="";
      else if(chipKey==="ac") n.acronym=null;
      else if(chipKey==="st") n.status=null;
      else if(chipKey.startsWith("cat_")) n.categories=(f.categories||[]).filter(c=>String(c.DOCTYPEID)!==chipKey.slice(4));
      else if(chipKey.startsWith("sp_"))  n.sponsors=(f.sponsors||[]).filter(s=>String(s.SPONSORID)!==chipKey.slice(3));
      else if(chipKey.startsWith("br_"))  n.brands=(f.brands||[]).filter(b=>String(b.BRANDID)!==chipKey.slice(3));
      else if(chipKey.startsWith("ind_")) n.indications=(f.indications||[]).filter(i=>String(i.INDICATIONID)!==chipKey.slice(4));
      if(!hasActiveFilters(n)) return null;
      return n;
    });
  };
  const allowedNav = NAV_ITEMS.filter(n => hasAccess(activeSub, n.key));
  const prevSubId = useRef(resolvedSub?.SUBPROJECTID);
  useEffect(()=>{
    if(activeSub?.SUBPROJECTID !== prevSubId.current){
      prevSubId.current = activeSub?.SUBPROJECTID;
      setActiveNav(getDefaultNav(activeSub));
    }
  },[activeSub?.SUBPROJECTID]);
  useEffect(()=>{
    if(activeSub?.SUBPROJECTID && activeNav)
      sessionStorage.setItem(NAV_KEY(activeSub.SUBPROJECTID), activeNav);
  },[activeNav, activeSub?.SUBPROJECTID]);
  useEffect(() => {
    const overridePages = ["usermanagement","admincorner","settings"];
    if (!overridePages.includes(activeNav) && !allowedNav.find(n => n.id === activeNav)) {
      const first = allowedNav.find(n => n.id !== "home");
      if (first) setActiveNav(first.id);
    }
  }, [activeSub?.SUBPROJECTID]);
  const handleSubChange = (e) => {
    const s = subs.find(x => String(x.SUBPROJECTID) === e.target.value);
    if (!s) return;
    sessionStorage.removeItem(NAV_KEY(s.SUBPROJECTID));
    setActiveSub(s);
    saveSession({ user, subproject: s });
    setActiveNav(getDefaultNav(s));
    // update URL so browser refresh restores this subproject
    window.history.replaceState({}, "", `${window.location.pathname}?sub=${s.SUBPROJECTID}`);
  };
  const logoColors = useLogoColors(activeSub?.APPLOGO || null);
  const theme      = buildTheme(logoColors);
  const [isMobile, setIsMobile] = useState(()=>window.innerWidth < 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  useEffect(()=>{
    const handler = ()=>setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return ()=>window.removeEventListener("resize", handler);
  },[]);
  return (
    <div style={{ height: "100vh", overflow: "hidden", background: C.bg, display: "flex", flexDirection: "column" }}>
      {/* ── HEADER ── */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, height: "48px", zIndex: 20,
        background: theme.headerBg, borderBottom: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        display: "flex", alignItems: "center", padding: "0 16px", gap: "10px",
        transition: "background 0.4s ease",
      }}>
        <div onClick={()=>{
            if(activeSub?.SUBPROJECTID) sessionStorage.removeItem(NAV_KEY(activeSub.SUBPROJECTID));
            if(hideNav){
              window.location.href=window.location.origin+window.location.pathname+(activeSub?`?sub=${activeSub.SUBPROJECTID}`:"");
            } else {
              if(allowNavOverride) setNavCleared(true);
              if(activeNav==="filter") setFilterApplied(null);
              setAiQuery(""); setShowAIChat(false);
              setActiveNav(getDefaultNav(activeSub));
            }
          }}
          style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:"8px" }}>
          {activeSub?.APPLOGO
            ? <img src={activeSub.APPLOGO.startsWith("http")?activeSub.APPLOGO:LOGO_BASE+activeSub.APPLOGO} alt="logo"
                style={{ height:"30px", width:"auto", maxWidth:"100px", borderRadius:"5px", objectFit:"contain" }}
                onError={e=>{ e.target.style.display="none"; e.target.nextSibling&&(e.target.nextSibling.style.display="flex"); }} />
            : <>
                <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg" style={{borderRadius:"7px",flexShrink:0}}>
                  <rect width="30" height="30" rx="7" fill={theme.primary||C.primary}/>
                  <text x="15" y="21" textAnchor="middle" fontFamily="Arial, sans-serif"
                    fontWeight="900" fontSize="17" fill="#fff">P</text>
                </svg>
              </>
          }
        </div>
        {!hideNav && !isMobile && <select value={activeSub?.SUBPROJECTID || ""} onChange={handleSubChange}
          style={{ padding: "6px 12px", borderRadius: "8px", border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.7)", color: C.dark, fontSize: "14px", fontWeight: "500", cursor: "pointer", outline: "none", maxWidth: "220px" }}>
          {subs.map(s => <option key={s.SUBPROJECTID} value={s.SUBPROJECTID}>{s.SUMMARY}</option>)}
        </select>}

        {/* ── NEW: AI search input ─────────────────────────────────────── */}
        {!hideSearchBar&&(
          <div style={{ flex:1, maxWidth:"800px", margin:"0 8px", position:"relative",
            display:"flex", alignItems:"center" }}>
            {/* Left icon */}
            <div style={{ position:"absolute", left:"12px", display:"flex",
              alignItems:"center", gap:"4px", pointerEvents:"none", zIndex:1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke={activeNav==="ai" ? theme.primary||C.primary : "#94A3B8"}
                strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <span style={{ fontSize:"9px", fontWeight:"800", letterSpacing:".5px",
                color: activeNav==="ai" ? theme.primary||C.primary : "#94A3B8" }}>AI</span>
            </div>
            <input
              value={aiQuery}
              onChange={e => setAiQuery(e.target.value)}
              onKeyDown={e => {
                if(e.key==="Enter" && aiQuery.trim()) {
                  if(hideNav) {
                    // Detail page — call API with mode=ai, show ChatPanel with result
                    const q = aiQuery.trim();
                    setShowAIChat(true);
                    setAiHistory([{ROLE:"user",VALUES_OF_CONTENT:q}]);
                    (async()=>{
                      try {
                        const fd=new FormData();
                        fd.append("review",q);
                        fd.append("subprojectid",activeSub?.SUBPROJECTID||"");
                        fd.append("userid",user?.USERID||"");
                        fd.append("mode","ai");
                        const r=await fetch("https://cip.medsphere.net/api/Vector_search",
                          {method:"POST",headers:{"Auth-Code":user?.AUTH_CODE||""},body:fd});
                        const res=await r.json();
                        if(res.STATUS===1){
                          if(res.THREAD) setAiThread(res.THREAD);
                          setAiHistory([
                            {ROLE:"user",    VALUES_OF_CONTENT:q},
                            {ROLE:"assistant",VALUES_OF_CONTENT:res.COMPLETION||""}
                          ]);
                        }
                      } catch(err){ console.error(err); }
                    })();
                  } else {
                    // Main page — documents in main area, chat overlay if AI
                    sessionStorage.setItem("pv_ai_prev_nav", activeNav);
                    setSearchKey(k=>k+1);
                    setShowAIChat(false);
                    setAiHistory([]); setAiThread(null);
                    setActiveNav("search");
                    sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), "search");
                  }
                }
                if(e.key==="Escape") {
                  setAiQuery("");
                  setShowAIChat(false);
                }
              }}
              onFocus={e => {
                e.target.style.borderColor = theme.primary||C.primary;
                e.target.style.boxShadow   = `0 0 0 3px ${theme.primary||C.primary}15`;
              }}
              onBlur={e => {
                if(activeNav!=="ai"){
                  e.target.style.borderColor = C.border;
                  e.target.style.boxShadow   = "none";
                }
              }}
              placeholder={hideNav ? "AI search…" : "Ask AI or search documents… (Enter to search)"}
              style={{ width:"100%", padding:"7px 36px 7px 46px", borderRadius:"10px",
                border:`1.5px solid ${activeNav==="ai" ? theme.primary||C.primary : C.border}`,
                background:"rgba(255,255,255,0.9)", fontSize:"13px", color:"#0F172A",
                outline:"none", fontFamily:"inherit", transition:"all .2s",
                boxShadow: activeNav==="ai" ? `0 0 0 3px ${theme.primary||C.primary}15` : "none" }}
            />
            {/* × clear inside input */}
            {aiQuery&&(
              <button onClick={()=>{
                  setAiQuery("");
                  setShowAIChat(false);
                  setAiHistory([]);
                  if(activeNav==="search"){
                    const prev = sessionStorage.getItem("pv_ai_prev_nav")||getDefaultNav(activeSub);
                    setActiveNav(prev);
                    sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), prev);
                  }
                }}
                style={{ position:"absolute", right:"10px", width:"18px", height:"18px",
                  borderRadius:"50%", border:"none", background:"#CBD5E1",
                  color:"#fff", cursor:"pointer", display:"flex", alignItems:"center",
                  justifyContent:"center", fontSize:"12px", lineHeight:1, zIndex:1 }}>
                ×
              </button>
            )}
          </div>
        )}
        {/* ───────────────────────────────────────────────────────────────── */}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          {/* DM button — hidden on mobile (in menu) */}
          {!isMobile&&<button onClick={()=>setShowDM(s=>!s)} title="Direct DM"
            style={{display:"flex",alignItems:"center",gap:"5px",padding:"5px 12px",
              borderRadius:"8px",
              border:`1.5px solid ${theme.primary||C.primary}${showDM?"":"44"}`,
              background:showDM?`${theme.primary||C.primary}`:`${theme.primary||C.primary}10`,
              color:showDM?"#fff":theme.primary||C.primary,
              fontSize:"12px",fontWeight:"700",cursor:"pointer",flexShrink:0,
              transition:"all 0.15s"}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            DM
          </button>}
          {!isMobile&&<BellButton user={user} activeSub={activeSub} color={theme.primary||C.primary}/>}
          {/* Filter + Quick filter — hidden on detail pages and review page and mobile */}
          {!isMobile&&!hideFilterButtons&&activeNav!=="review"&&<>
            <button title="Advanced Filter" onClick={()=>setFilterOpen(o=>!o)}
              style={{width:"30px",height:"30px",borderRadius:"7px",
                border:`1px solid ${filterApplied?theme.primary||C.primary:C.border}`,
                background:filterApplied?`${theme.primary||C.primary}15`:"rgba(255,255,255,0.7)",
                display:"flex",alignItems:"center",justifyContent:"center",
                cursor:"pointer",position:"relative"}}>
              {filterApplied&&<span style={{position:"absolute",top:"-3px",right:"-3px",
                width:"7px",height:"7px",borderRadius:"50%",
                background:theme.primary||C.primary,border:"2px solid #fff"}}/>}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke={filterApplied?theme.primary||C.primary:C.muted} strokeWidth="2" strokeLinecap="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
            </button>
            {filterApplied&&hasActiveFilters(filterApplied)&&(
              <button onClick={()=>{
                  setFilterApplied(null);
                  const returnNav = prevNavRef.current || getDefaultNav(activeSub);
                  prevNavRef.current = null;
                  setActiveNav(returnNav);
                  sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), returnNav);
                }}
                title="Clear filter"
                style={{width:"20px",height:"20px",borderRadius:"50%",border:"none",
                  background:`${theme.primary||C.primary}20`,color:theme.primary||C.primary,
                  cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:"14px",fontWeight:"700",marginLeft:"-6px"}}>×</button>
            )}
            {activeSub?.ROLE===1&&(
              <button
                onClick={()=>{ setActiveNav("review"); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),"review"); }}
                title="Review & Publish — drafts last 7 days"
                style={{width:"30px",height:"30px",borderRadius:"7px",
                  border:`1px solid ${activeNav==="review"?theme.primary||C.primary:C.border}`,
                  background:activeNav==="review"?`${theme.primary||C.primary}15`:"rgba(255,255,255,0.7)",
                  display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke={activeNav==="review"?theme.primary||C.primary:C.muted} strokeWidth="2" strokeLinecap="round">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
              </button>
            )}
          </>}
          {!isMobile&&<button
            onClick={()=>{
              if(activeNav==="echo"){
                window.dispatchEvent(new CustomEvent("echo-capture"));
              } else {
                const base=window.location.origin+window.location.pathname;
                window.open(`${base}?capture=1&sub=${activeSub?.SUBPROJECTID||""}`, "_blank", "noopener,noreferrer");
              }
            }}
            title={activeNav==="echo"?"Echo Capture":"Capture new document"}
            style={{ display:"flex",alignItems:"center",gap:"5px",padding:"5px 12px",
              borderRadius:"8px",border:`1.5px solid ${theme.primary||C.primary}44`,
              background:`${theme.primary||C.primary}10`,color:theme.primary||C.primary,
              fontSize:"12px",fontWeight:"700",cursor:"pointer",flexShrink:0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            {activeNav==="echo" ? "Echo Capture" : "Capture"}
          </button>}
          <div ref={profileRef} style={{ position: "relative" }}>
            <button onClick={() => setShowProfile(!showProfile)}
              style={{ width: "30px", height: "30px", borderRadius: "50%", background: theme.avatarBg, border: "none", color: "#fff", fontSize: "12px", fontWeight: "500", cursor: "pointer", letterSpacing: "0.5px", transition: "background 0.4s" }}>
              {(user.FIRSTNAME?.[0] || "") + (user.LASTNAME?.[0] || "")}
            </button>
            {showProfile && (
              <div style={{ position: "absolute", right: 0, top: "42px", background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, padding: "1rem", width: "230px", zIndex: 100, boxShadow: "0 8px 24px rgba(4,44,83,0.1)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                  <div style={{ width: "38px", height: "38px", borderRadius: "50%", background: theme.avatarBg, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "14px", fontWeight: "500" }}>
                    {(user.FIRSTNAME?.[0] || "") + (user.LASTNAME?.[0] || "")}
                  </div>
                  <div>
                    <div onClick={()=>{ setNavCleared(true); setActiveNav("settings"); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),"settings"); setShowProfile(false); }}
                      style={{ fontSize:"15px",fontWeight:"500",color:C.text,
                        cursor:"pointer",textDecoration:"underline",
                        textDecorationStyle:"dotted",textDecorationColor:C.border }}>
                      {user.FIRSTNAME} {user.LASTNAME}
                    </div>
                    <div style={{ fontSize: "12px", color: C.muted }}>{user.PROJECTNAME}</div>
                  </div>
                </div>
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
                  {[].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0" }}>
                      <span style={{ color: C.muted }}>{k}</span>
                      <span style={{ color: C.text, fontWeight: "500" }}>{v}</span>
                    </div>
                  ))}
                </div>
                {activeSub?.ROLE === 1 && (
                  <button
                    onClick={() => { setNavCleared(true); setActiveNav("admincorner"); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), "admincorner"); setShowProfile(false); }}
                    style={{ width: "100%", marginTop: "8px", padding: "8px", borderRadius: "7px",
                      border: `1px solid ${C.border}`, background: "#f4f7fb",
                      color: "#0d1b2a", fontSize: "14px", cursor: "pointer", fontWeight: "600",
                      display: "flex", alignItems: "center", gap: "8px" }}>
                    ⚙️ Admin Corner
                  </button>
                )}
                {activeSub?.ADMINROLE === 1 && (
                  <button
                    onClick={() => { setNavCleared(true); setActiveNav("usermanagement"); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), "usermanagement"); setShowProfile(false); }}
                    style={{ width: "100%", marginTop: "8px", padding: "8px", borderRadius: "7px",
                      border: `1px solid ${C.border}`, background: "#f4f7fb",
                      color: "#0d1b2a", fontSize: "14px", cursor: "pointer", fontWeight: "600",
                      display: "flex", alignItems: "center", gap: "8px" }}>
                    👥 Account
                  </button>
                )}
                <button onClick={()=>{
                    // clear all nav session keys
                    Object.keys(sessionStorage).filter(k=>k.startsWith("pv_nav_")).forEach(k=>sessionStorage.removeItem(k));
                    // clear URL
                    window.history.replaceState({}, "", window.location.pathname);
                    onLogout();
                  }}
                  style={{ width: "100%", marginTop: "8px", padding: "8px", borderRadius: "7px", border: `1px solid #F09595`, background: C.errorBg, color: C.error, fontSize: "14px", cursor: "pointer", fontWeight: "500" }}>
                  Sign out
                </button>
                <div style={{display:"flex",gap:"6px",marginTop:"8px"}}>
                  <button onClick={()=>{ setShowPrivacy(true); setShowProfile(false); }}
                    style={{ flex:1, padding:"8px", borderRadius:"7px",
                      border:`1px solid ${C.border}`, background:"#f4f7fb",
                      color:"#0d1b2a", fontSize:"13px", cursor:"pointer", fontWeight:"500" }}>
                    Privacy Policy
                  </button>
                  <a href="mailto:info@medsphere.net"
                    onClick={()=>setShowProfile(false)}
                    style={{ flex:1, padding:"8px", borderRadius:"7px",
                      border:`1px solid ${C.border}`, background:"#f4f7fb",
                      color:"#0d1b2a", fontSize:"13px", cursor:"pointer", fontWeight:"500",
                      textAlign:"center", textDecoration:"none", display:"block" }}>
                    Contact Us
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* ── BODY ── */}
      <div style={{ display: "flex", position: "fixed", top: "48px", left: 0, right: 0, bottom: isMobile&&!hideNav ? "56px" : 0 }}>
        {/* ── SIDEBAR — desktop only ── */}
        {!hideNav && !isMobile && <div data-leftnav="1" style={{
          width: "200px", flexShrink: 0,
          background: "#F7F9FC", borderRight: "1px solid #E4EAF2",
          display: "flex", flexDirection: "column",
          overflowY: "auto", overflowX: "hidden",
        }}>
          {allowedNav.map(n => (
            <NavItem key={n.id} item={n} active={activeNav === n.id} onClick={() => { if(allowNavOverride) setNavCleared(true); if(activeNav==="filter") setFilterApplied(null); setAiQuery(""); setShowAIChat(false); setActiveNav(n.id); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), n.id); }} theme={theme} />
          ))}
          <div style={{ marginTop: "auto", padding: "8px 12px", borderTop: "1px solid #E4EAF2" }}>
            <div style={{ fontSize: "12px", color: "#A0AABB" }}>{user.PROJECTNAME}</div>
            <div style={{ fontSize: "13px", color: "#4A5568", fontWeight: "500", marginTop: "2px" }}>{user.FIRSTNAME} {user.LASTNAME}</div>
          </div>
        </div>}
        {/* ── CONTENT ── */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: C.bg, display:"flex", flexDirection:"column", position:"relative" }}>
          <ThemeCtx.Provider value={{ theme, activeSub, user, isMobile }}>
            {filterApplied&&hasActiveFilters(filterApplied)&&activeNav==="filter"&&(
              <div style={{position:"sticky",top:0,zIndex:10}}>
                <FilterChipsBar
                  filters={filterApplied}
                  color={theme.primary||C.primary}
                  onRemove={removeFilterChip}
                  onClearAll={()=>{
                    setFilterApplied(null);
                    const returnNav = prevNavRef.current || getDefaultNav(activeSub);
                    prevNavRef.current = null;
                    setActiveNav(returnNav);
                    sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), returnNav);
                  }}/>
              </div>
            )}
            {overrideContent && !navCleared
              ? overrideContent
              : <AppContent user={user} activeSub={activeSub} theme={theme}
                  activeNav={activeNav}
                  setActiveNav={setActiveNav} navSort={navSort} setNavSort={setNavSort}
                  filterApplied={filterApplied} aiQuery={aiQuery} searchKey={searchKey}
                  onAIResponse={({history,thread})=>{ setAiHistory(history); setAiThread(thread); setShowAIChat(true); }}/>}
          {filterOpen&&activeSub&&(
            <FilterPanel
              subproject={activeSub} user={user}
              color={theme.primary||C.primary}
              isAdmin={activeSub?.ROLE===1}
              onClose={()=>setFilterOpen(false)}
              initialFilters={filterApplied||EMPTY_FILTER}
              onApply={(filters)=>{
                prevNavRef.current = activeNav;
                setFilterApplied(filters);
                setFilterOpen(false);
                setActiveNav("filter");
                sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),"filter");
              }}/>
          )}
          {showAIChat&&!hideNav&&(
            <AIChatOverlay
              history={aiHistory}
              thread={aiThread}
              setThread={setAiThread}
              user={user}
              subproject={activeSub}
              color={theme.primary||C.primary}
              width={aiChatWidth}
              minimized={aiMinimized}
              onMinimize={()=>setAiMinimized(true)}
              onRestore={()=>setAiMinimized(false)}
              onClose={()=>{
                setShowAIChat(false); setAiHistory([]); setAiThread(null);
                setAiQuery(""); setAiMinimized(false);
                const prev=sessionStorage.getItem("pv_ai_prev_nav")||getDefaultNav(activeSub);
                setActiveNav(prev); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),prev);
              }}/>
          )}
                    {/* AI Chat overlay — detail pages (hideNav=true) */}
          {showAIChat&&hideNav&&(
            <>
              <div onClick={()=>setShowAIChat(false)}
                style={{position:"fixed",inset:0,background:"transparent",
                  zIndex:800}}/>
              <AIChatOverlay
                history={aiHistory}
                thread={aiThread}
                setThread={setAiThread}
                user={user}
                subproject={activeSub}
                color={theme.primary||C.primary}
                width={680}
                onClose={()=>{ setShowAIChat(false); setAiHistory([]); setAiThread(null); setAiQuery(""); setAiMinimized(false); }}/>
            </>
          )}
          {showDM&&<div onMouseDown={()=>setShowDM(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.2)",zIndex:999,cursor:"pointer"}}/>}
          {showDM&&(
            <div ref={dmRef} onMouseDown={e=>e.stopPropagation()} style={{position:"fixed",top:0,right:0,bottom:0,width:"360px",
              background:"#fff",boxShadow:"-4px 0 24px rgba(0,0,0,0.15)",
              zIndex:1000,display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"12px 14px",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>
                <span style={{fontSize:"15px",fontWeight:"700",color:"#111"}}>💬 DM</span>
                <button onClick={()=>setShowDM(false)}
                  style={{background:"none",border:"none",cursor:"pointer",
                    fontSize:"20px",color:"#9CA3AF",lineHeight:1,padding:0}}>×</button>
              </div>
              <div style={{flex:1,overflow:"hidden"}}>
                <DMPanel user={user} activeSub={activeSub} onClose={()=>setShowDM(false)}/>
              </div>
            </div>
          )}
          {showAdminCorner && (
            <AdminCorner
              user={user}
              activeSub={activeSub}
              onClose={() => setShowAdminCorner(false)}
            />
          )}
          
          {showPrivacy&&(
            <>
              <div onMouseDown={()=>setShowPrivacy(false)}
                style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:2000}}/>
              <div onMouseDown={e=>e.stopPropagation()}
                style={{position:"fixed",top:"50%",left:"50%",
                  transform:"translate(-50%,-50%)",
                  width:"min(680px,92vw)",maxHeight:"85vh",
                  background:"#fff",borderRadius:12,zIndex:2001,
                  boxShadow:"0 20px 60px rgba(0,0,0,0.2)",
                  display:"flex",flexDirection:"column",overflow:"hidden"}}>
                {/* header */}
                <div style={{padding:"14px 20px",borderBottom:"1px solid #E5E7EB",
                  display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                  <div style={{fontSize:15,fontWeight:700,color:"#111"}}>Privacy Policy</div>
                  <button onClick={()=>setShowPrivacy(false)}
                    style={{background:"none",border:"none",cursor:"pointer",
                      fontSize:20,color:"#9CA3AF",padding:0,lineHeight:1}}>×</button>
                </div>
                {/* scrollable content */}
                <div style={{flex:1,overflow:"hidden"}}>
                  <iframe
                    src="/privacy_policy.html"
                    style={{width:"100%",height:"100%",border:"none"}}
                    title="Privacy Policy"
                  />
                </div>
              </div>
            </>
          )}
          </ThemeCtx.Provider>
        </div>
      </div>

      {/* ── MOBILE BOTTOM NAV ── */}
      {!hideNav && isMobile && (
        <div style={{position:"fixed",bottom:0,left:0,right:0,height:"56px",
          background:"#fff",borderTop:"1px solid #E4EAF2",
          display:"flex",alignItems:"stretch",zIndex:500,
          boxShadow:"0 -2px 10px rgba(0,0,0,0.08)"}}>

          {/* Priority nav items based on access */}
          {(()=>{
            const priority = ["news","clinicaltrials","reports","upcoming","newsletter","ataglance","home"];
            const bottom = priority.filter(id=>allowedNav.find(n=>n.id===id)).slice(0,4);
            const icons = {
              news:         <><path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2"/></>,
              clinicaltrials:<><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v11m0 0h10M9 14l-4 4 4 4"/></>,
              reports:      <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
              upcoming:     <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
              newsletter:   <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
              ataglance:    <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
              home:         <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></>,
            };
            const labels = {
              news:"News", clinicaltrials:"Trials", reports:"Reports",
              upcoming:"Events", newsletter:"NewsFlash", ataglance:"Glance", home:"Home"
            };
            return bottom.map(id=>{
              const active = activeNav===id;
              const col = active?(theme.primary||C.primary):"#94A3B8";
              return(
                <button key={id}
                  onClick={()=>{
                    if(activeNav==="filter") setFilterApplied(null);
                    setAiQuery(""); setShowAIChat(false);
                    setActiveNav(id);
                    sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),id);
                  }}
                  style={{flex:1,border:"none",background:"none",cursor:"pointer",
                    display:"flex",flexDirection:"column",alignItems:"center",
                    justifyContent:"center",gap:"2px",padding:"4px 2px",
                    borderTop:active?`2px solid ${col}`:"2px solid transparent"}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke={col} strokeWidth={active?"2.5":"1.8"} strokeLinecap="round" strokeLinejoin="round">
                    {icons[id]}
                  </svg>
                  <span style={{fontSize:"9px",fontWeight:active?"700":"500",color:col,lineHeight:1}}>
                    {labels[id]}
                  </span>
                </button>
              );
            });
          })()}

          {/* Review & Publish — admin only, between last item and Menu */}
          {activeSub?.ROLE===1&&(()=>{
            const active=activeNav==="review";
            const col=active?(theme.primary||C.primary):"#94A3B8";
            return(
              <button onClick={()=>{
                  if(activeNav==="filter") setFilterApplied(null);
                  setActiveNav("review");
                  sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),"review");
                }}
                style={{flex:1,border:"none",background:"none",cursor:"pointer",
                  display:"flex",flexDirection:"column",alignItems:"center",
                  justifyContent:"center",gap:"2px",padding:"4px 2px",
                  borderTop:active?`2px solid ${col}`:"2px solid transparent"}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={col} strokeWidth={active?"2.5":"1.8"} strokeLinecap="round">
                  <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
                <span style={{fontSize:"9px",fontWeight:active?"700":"500",color:col}}>Review</span>
              </button>
            );
          })()}

          {/* More / Menu button */}
          <button onClick={()=>setShowMobileMenu(m=>!m)}
            style={{flex:1,border:"none",background:showMobileMenu?`${theme.primary||C.primary}10`:"none",
              cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",
              justifyContent:"center",gap:"2px",padding:"4px 2px",
              borderTop:showMobileMenu?`2px solid ${theme.primary||C.primary}`:"2px solid transparent"}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={showMobileMenu?theme.primary||C.primary:"#94A3B8"} strokeWidth="1.8" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
            <span style={{fontSize:"9px",color:showMobileMenu?theme.primary||C.primary:"#94A3B8"}}>Menu</span>
          </button>
        </div>
      )}

      {/* ── MOBILE MENU SHEET ── */}
      {isMobile && showMobileMenu && (
        <>
          <div onClick={()=>setShowMobileMenu(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:600}}/>
          <div style={{position:"fixed",bottom:"56px",left:0,right:0,
            background:"#fff",borderRadius:"16px 16px 0 0",zIndex:601,
            boxShadow:"0 -4px 20px rgba(0,0,0,0.15)"}}>

            {/* Franchise selector */}
            {subs.length>1&&(
              <div style={{padding:"8px 16px",borderBottom:"1px solid #F1F5F9"}}>
                <div style={{fontSize:"10px",color:"#9CA3AF",fontWeight:"600",marginBottom:"4px",textTransform:"uppercase",letterSpacing:"0.5px"}}>Franchise</div>
                <select value={activeSub?.SUBPROJECTID||""} onChange={e=>{
                    setShowMobileMenu(false);
                    handleSubChange(e);
                  }}
                  style={{width:"100%",padding:"8px 10px",borderRadius:"8px",
                    border:"1px solid #E5E7EB",background:"#fff",fontSize:"14px",
                    fontWeight:"500",color:C.dark,outline:"none"}}>
                  {subs.map(s=><option key={s.SUBPROJECTID} value={s.SUBPROJECTID}>{s.SUMMARY}</option>)}
                </select>
              </div>
            )}

            {/* Action buttons row */}
            <div style={{display:"flex",borderBottom:"1px solid #F1F5F9",padding:"12px 16px",gap:"8px",flexWrap:"wrap"}}>
              <button onClick={()=>{ setShowDM(true); setShowMobileMenu(false); }}
                style={{flex:1,minWidth:"60px",padding:"10px 8px",borderRadius:"10px",border:"none",
                  background:`${theme.primary||C.primary}10`,color:theme.primary||C.primary,
                  fontSize:"11px",fontWeight:"700",cursor:"pointer",
                  display:"flex",flexDirection:"column",alignItems:"center",gap:"4px"}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                DM
              </button>
              <button onClick={()=>{ setFilterOpen(true); setShowMobileMenu(false); }}
                style={{flex:1,minWidth:"60px",padding:"10px 8px",borderRadius:"10px",border:"none",
                  background:filterApplied?`${theme.primary||C.primary}10`:"#F8FAFC",
                  color:filterApplied?theme.primary||C.primary:"#64748B",
                  fontSize:"11px",fontWeight:"700",cursor:"pointer",position:"relative",
                  display:"flex",flexDirection:"column",alignItems:"center",gap:"4px"}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                Filter
              </button>
              <button onClick={()=>{
                  setShowMobileMenu(false);
                  const base=window.location.origin+window.location.pathname;
                  window.open(`${base}?capture=1&sub=${activeSub?.SUBPROJECTID||""}`,"_blank","noopener,noreferrer");
                }}
                style={{flex:1,minWidth:"60px",padding:"10px 8px",borderRadius:"10px",border:"none",
                  background:`${theme.primary||C.primary}10`,color:theme.primary||C.primary,
                  fontSize:"11px",fontWeight:"700",cursor:"pointer",
                  display:"flex",flexDirection:"column",alignItems:"center",gap:"4px"}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Capture
              </button>
              {activeSub?.ROLE===1&&(
                <button onClick={()=>{
                    setShowMobileMenu(false);
                    setActiveNav("review");
                    sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),"review");
                  }}
                  style={{flex:1,minWidth:"60px",padding:"10px 8px",borderRadius:"10px",border:"none",
                    background:activeNav==="review"?`${theme.primary||C.primary}10`:"#F8FAFC",
                    color:activeNav==="review"?theme.primary||C.primary:"#64748B",
                    fontSize:"11px",fontWeight:"700",cursor:"pointer",
                    display:"flex",flexDirection:"column",alignItems:"center",gap:"4px"}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                  </svg>
                  Review
                </button>
              )}
              <button onClick={()=>{
                  setActiveNav("settings");
                  sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),"settings");
                  setShowMobileMenu(false);
                }}
                style={{flex:1,minWidth:"60px",padding:"10px 8px",borderRadius:"10px",border:"none",
                  background:"#F8FAFC",color:"#64748B",fontSize:"11px",fontWeight:"700",
                  cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:"4px"}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                Profile
              </button>
            </div>

            {/* All nav items */}
            <div style={{maxHeight:"45vh",overflowY:"auto",padding:"6px 0"}}>
              {allowedNav.map(n=>(
                <button key={n.id}
                  onClick={()=>{
                    setShowMobileMenu(false);
                    if(activeNav==="filter") setFilterApplied(null);
                    setActiveNav(n.id);
                    sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),n.id);
                  }}
                  style={{width:"100%",padding:"11px 20px",border:"none",
                    background:activeNav===n.id?`${theme.primary||C.primary}08`:"none",
                    cursor:"pointer",display:"flex",alignItems:"center",gap:"12px",
                    textAlign:"left",fontSize:"14px",fontWeight:activeNav===n.id?"700":"500",
                    color:activeNav===n.id?theme.primary||C.primary:C.text,
                    borderLeft:activeNav===n.id?`3px solid ${theme.primary||C.primary}`:"3px solid transparent"}}>
                  {n.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
// ─── Screen router ────────────────────────────────────────────────────────────
function AppContent({ user, activeSub, theme, activeNav, setActiveNav, navSort, setNavSort, filterApplied, aiQuery, onAIResponse, searchKey }) {
  const navigate = (nav, sort=null) => { setNavSort(sort); setActiveNav(nav); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), nav); };
  switch (activeNav) {
    case "search":         return <VectorSearch key={"vs-"+searchKey} user={user} subproject={activeSub} initialQuery={aiQuery} onAIResponse={onAIResponse}/>;
    case "ataglance":      return <AtAGlance user={user} subproject={activeSub} onNavigate={navigate} />;
    case "timelines":      return <TimelinesScreen user={user} subproject={activeSub} />;
    case "echo":           return <EchoScreen user={user} subproject={activeSub} />;
    case "review":         return <ReviewPublish user={user} subproject={activeSub} />;
    case "filter":         return <AdvancedFilterScreen user={user} subproject={activeSub} appliedFilters={filterApplied}/>;
    case "gallery":        return <GalleryScreen user={user} subproject={activeSub} defaultSort={navSort} />;
    case "productprofile": return <ProductProfileScreen user={user} subproject={activeSub} />;
    case "news":           return <ListScreen user={user} subproject={activeSub} title="News"      docTypes={NEWS_TYPES}    />;
    case "reports":        return <ListScreen user={user} subproject={activeSub} title="Reports"   docTypes={REPORTS_TYPES} />;
    case "newsletter":     return <NewsFlashScreen user={user} subproject={activeSub} />;
    case "workshop":       return <WorkshopScreen user={user} subproject={activeSub} />;
    case "admincorner":    return <AdminCorner user={user} activeSub={activeSub} onNavigate={(nav) => { setNavSort(null); setActiveNav(nav); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), nav); }} />;
    case "usermanagement": return <UserManagement user={user} activeSub={activeSub} onNavigate={(nav) => { setNavSort(null); setActiveNav(nav); sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID), nav); }} />;
    case "settings":       return <UserSettings user={user} activeSub={activeSub} theme={theme} onClose={()=>{
      sessionStorage.removeItem(NAV_KEY(activeSub?.SUBPROJECTID));
      const nav=getDefaultNav(activeSub);
      setActiveNav(nav);
      sessionStorage.setItem(NAV_KEY(activeSub?.SUBPROJECTID),nav);
    }} />;
    case "congress": {
      let ci = {};
      try { ci = JSON.parse(sessionStorage.getItem("pv_congress_init")||"{}"); } catch(e){}
      return <CongressScreen user={user} subproject={activeSub}
        initCongressId={ci.congressId||null}
        initPresentationId={ci.presentationId||null}
        initDocId={ci.docId||null}
        initDoctypeId={ci.doctypeId||null}
        initCommentId={ci.commentId||null}
      />;
    }
    case "kits":           return <ListScreen user={user} subproject={activeSub} title="KITS/KIQS" docTypes={KITS_TYPES}    />;
    case "clinicaltrials": return <ClinicalTrialsScreen user={user} subproject={activeSub} />;
    case "past":           return <EventsScreen user={user} subproject={activeSub} eventtype="past" />;
    case "upcoming":       return <EventsScreen user={user} subproject={activeSub} eventtype="upcoming" />;
    case "home":           return <DashboardHome user={user} subproject={activeSub} onNavigate={(nav)=>{ setNavSort(null); setActiveNav(nav); }} />;
    default:
      return (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
          height:"100%", flexDirection:"column", gap:"8px" }}>
          <Icon name="gallery" size={40} color={C.border} />
          <div style={{ fontSize:"15px", color:C.muted, fontWeight:"500" }}>
            {NAV_ITEMS.find(n => n.id === activeNav)?.label}
          </div>
          <div style={{ fontSize:"13px", color:C.hint }}>Content will appear here</div>
        </div>
      );
  }
}